import React, { useEffect, useState } from 'react'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { ServiceCard } from '../components/ServiceCard'
import { CTASection } from '../components/CTASection'
import { Testimonial } from '../components/Testimonial'
import { homeServices, homeTestimonials } from '../data/mockData'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { CheckCircle2Icon, CalendarIcon, ClockIcon } from 'lucide-react'
import { SeoHelmet } from '../components/SeoHelmet'
import { ROUTE_META } from '../data/routeMeta'
import { getBlogPosts, type BlogPost } from '../services/outline'
import { slugify } from '../utils/slugify'

export function Home() {
  const [recentPosts, setRecentPosts] = useState<BlogPost[]>([])
  const [showBlogSection, setShowBlogSection] = useState(false)

  useEffect(() => {
    let cancelled = false
    getBlogPosts(3, 0)
      .then((result) => {
        if (cancelled) return
        if (result.posts && result.posts.length > 0) {
          setRecentPosts(result.posts)
          setShowBlogSection(true)
        }
      })
      .catch(() => {
        // Swallow: section stays hidden on error
      })
    return () => { cancelled = true }
  }, [])

  return (
    <div className="min-h-screen w-full bg-white flex flex-col font-['Roboto']">
      <SeoHelmet {...ROUTE_META['/']} url="/" pageType="website" />
      <Header />

      <main className="flex-1">
        <section className="w-full bg-light-background overflow-hidden relative">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col lg:flex-row items-center py-20 lg:py-32 gap-12">
              <motion.div
                initial={{ opacity: 0, x: -50 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                className="w-full lg:w-1/2 z-10"
              >
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight font-['Poppins']">
                  <span className="text-primary">AI services</span>{' '}
                  <span className="text-gray-900">that work for you</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed mb-10 max-w-lg">
                  Move past the AI hype. We help you build production-ready
                  software, adopt AI without compromising your data, and train
                  your teams to actually use it.
                </p>
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to="/consulting"
                    className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-lg text-white bg-primary hover:bg-primary-dark transition-colors shadow-lg hover:shadow-xl"
                  >
                    Get Started
                  </Link>
                  <a
                    href="#services"
                    className="inline-flex items-center justify-center px-8 py-4 text-base font-medium rounded-lg text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 transition-colors shadow-sm"
                  >
                    Learn More
                  </a>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="w-full lg:w-1/2 relative"
              >
                <div className="relative rounded-2xl overflow-hidden shadow-2xl aspect-[4/3]">
                  <img
                    src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=1200&q=80"
                    alt="Team collaborating in a modern office"
                    className="object-cover w-full h-full"
                  />
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/20 to-transparent mix-blend-overlay" />
                </div>
                <div className="absolute -bottom-6 -left-6 w-24 h-24 bg-primary rounded-full mix-blend-multiply filter blur-xl opacity-50 animate-pulse" />
                <div
                  className="absolute -top-6 -right-6 w-32 h-32 bg-blue-400 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse"
                  style={{ animationDelay: '1s' }}
                />
              </motion.div>
            </div>
          </div>
        </section>

        <section className="w-full py-20 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex flex-col items-center text-center"
              >
                <CheckCircle2Icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2 font-['Poppins']">
                  Production-ready
                </h3>
                <p className="text-gray-600">
                  We don't ship demos. Every feature goes from UX insight to
                  production, built to scale and maintain.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="flex flex-col items-center text-center"
              >
                <CheckCircle2Icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2 font-['Poppins']">
                  Absolute privacy
                </h3>
                <p className="text-gray-600">
                  We architect modular, open-source stacks so you never give
                  away your data or lock into a single vendor.
                </p>
              </motion.div>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="flex flex-col items-center text-center"
              >
                <CheckCircle2Icon className="w-10 h-10 text-primary mb-4" />
                <h3 className="text-xl font-bold text-gray-900 mb-2 font-['Poppins']">
                  Practical workflows
                </h3>
                <p className="text-gray-600">
                  Our training is hands-on and role-specific, so your teams
                  leave with workflows they use the very next day.
                </p>
              </motion.div>
            </div>
          </div>
        </section>

        <section
          id="services"
          className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 relative"
        >
          <div className="max-w-7xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-16"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 font-['Poppins']">
                Our services
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto">
                Comprehensive AI solutions tailored to your specific needs.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {homeServices.map((service, index) => (
                <Link key={index} to={service.link} className="block">
                  <ServiceCard
                    title={service.title}
                    description={service.description}
                    icon={service.icon}
                    delay={service.delay}
                  />
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center"
            >
              {homeTestimonials.map((testimonial, index) => (
                <Testimonial
                  key={index}
                  quote={testimonial.quote}
                  photoUrl={testimonial.photoUrl}
                  name={testimonial.name}
                  title={testimonial.title}
                  company={testimonial.company}
                />
              ))}
            </motion.div>
          </div>
        </section> */}

        {showBlogSection && (
          <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-gray-50 border-t border-gray-100">
            <div className="max-w-7xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="flex flex-col sm:flex-row justify-between items-center mb-12"
              >
                <div>
                  <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 font-['Poppins']">
                    Latest from our blog
                  </h2>
                  <p className="text-xl text-gray-600 max-w-2xl">
                    Insights on building production-ready AI and maintaining data privacy.
                  </p>
                </div>
                <Link
                  to="/blog"
                  className="mt-6 sm:mt-0 inline-flex items-center font-medium text-primary hover:text-primary-dark transition-colors"
                >
                  View all articles
                  <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                  </svg>
                </Link>
              </motion.div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                {recentPosts.map((post, index) => {
                  const date = new Date(post.publishedAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })
                  return (
                    <motion.article
                      key={post.id}
                      initial={{ opacity: 0, y: 20 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 hover:shadow-md transition-shadow flex flex-col h-full"
                    >
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <div className="flex items-center gap-1">
                          <CalendarIcon className="w-4 h-4" />
                          <span>{date}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <ClockIcon className="w-4 h-4" />
                          <span>{post.readTime}</span>
                        </div>
                      </div>

                      <Link to={`/blog/${post.id}/${slugify(post.title)}`} className="block group mb-4">
                        <h3 className="text-xl font-bold text-gray-900 font-['Poppins'] group-hover:text-primary transition-colors line-clamp-2">
                          {post.title}
                        </h3>
                      </Link>

                      <p className="text-gray-600 mb-6 leading-relaxed flex-grow line-clamp-3">
                        {post.excerpt}
                      </p>

                      <Link
                        to={`/blog/${post.id}/${slugify(post.title)}`}
                        className="inline-flex items-center font-medium text-primary hover:text-primary-dark transition-colors mt-auto"
                      >
                        Read full article
                        <svg className="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
                        </svg>
                      </Link>
                    </motion.article>
                  )
                })}
              </div>
            </div>
          </section>
        )}

        <CTASection />
      </main>

      <Footer />
    </div>
  )
}